import { useEffect, useState } from 'react';
import { getPetCard, getDocuments } from './api';
import SectionHeader from './SectionHeader';
import WeightBlock from './WeightBlock';
import CareBlock from './CareBlock';
import TreatmentBlock from './TreatmentBlock';
import VisitsBlock from './VisitsBlock';
import NutritionBlock from './NutritionBlock';
import ScheduleBlock from './ScheduleBlock';
import ContactsBlock from './ContactsBlock';
import LabBlock from './LabBlock';
import PetSitterBlock from './PetSitterBlock';
import FoodReactionBlock from './FoodReactionBlock';
import RecentTreatmentsBlock from './RecentTreatmentsBlock';
import { UploadModal, SectionDocsModal } from './DocumentModal';
import './PetCard.css';

const NAV_ITEMS = [
  { id: 'section-vacc',      icon: '💉', label: 'Обработки' },
  { id: 'section-weight',    icon: '⚖️', label: 'Вес' },
  { id: 'section-care',      icon: '✂️', label: 'Уход' },
  { id: 'section-lab',       icon: '🔬', label: 'Анализы' },
  { id: 'section-treatment', icon: '💊', label: 'Лечение' },
  { id: 'section-visits',   icon: '🏥', label: 'Визиты' },
  { id: 'section-nutrition',       icon: '🥩', label: 'Питание' },
  { id: 'section-food-reactions', icon: '🍽️', label: 'Реакции' },
  { id: 'section-schedule',       icon: '📅', label: 'Расписание' },
  { id: 'section-contacts',  icon: '📞', label: 'Контакты' },
];

function NavBar({ onUpload, onNavigate }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="nav-bar">
      {NAV_ITEMS.map((item) => (
        <button key={item.id} className="nav-item" onClick={() => scrollTo(item.id)}>
          <span className="nav-icon">{item.icon}</span>
          <span className="nav-label">{item.label}</span>
        </button>
      ))}
      {onUpload && (
        <button className="nav-item nav-item-attach" onClick={onUpload}>
          <span className="nav-icon">📎</span>
          <span className="nav-label">Документ</span>
        </button>
      )}
      <button className="nav-item nav-item-attach" onClick={() => onNavigate('petsitter')}>
        <span className="nav-icon">🤝</span>
        <span className="nav-label">Пет ситтер</span>
      </button>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="info-row">
      <span className="info-label">{label}</span>
      <span className="info-value">{value}</span>
    </div>
  );
}

function groupBySection(docs) {
  const map = {};
  for (const doc of docs) {
    if (!map[doc.section]) map[doc.section] = [];
    map[doc.section].push(doc);
  }
  return map;
}

export default function PetCard({ petId }) {
  const [pet, setPet] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewSection, setViewSection] = useState(null);
  const [page, setPage] = useState('main');

  useEffect(() => {
    if (!petId) return;
    getPetCard(petId)
      .then((data) => {
        setPet(data);
        if (data.role === 'petsitter') setPage('petsitter');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [petId]);

  useEffect(() => {
    if (!petId) return;
    if (pet?.role === 'petsitter') return; // petsitter has no access to documents
    getDocuments(petId)
      .then((list) => setDocs(groupBySection(list)))
      .catch(() => {});
  }, [petId, pet?.role]);

  const handleUploaded = (doc) => {
    setDocs((prev) => ({
      ...prev,
      [doc.section]: [doc, ...(prev[doc.section] || [])],
    }));
  };

  const handleDeleted = (section, docId) => {
    setDocs((prev) => ({
      ...prev,
      [section]: (prev[section] || []).filter((d) => d.id !== docId),
    }));
  };

  if (loading) return <div className="loading">Загрузка...</div>;
  if (error) return <div className="error">Ошибка: {error}</div>;
  if (!pet) return null;

  const docProps = { docs, onDocsClick: (key) => setViewSection(key) };

  // ── Страница Пет Ситтера ──────────────────────────────────────────────────
  if (page === 'petsitter') {
    const isPetSitterOnly = pet.role === 'petsitter';
    return (
      <div className="pet-card">
        <div className="pet-banner subpage-banner">
          <img src="/banner.jpg" alt="banner" className="banner-img" />
          <div className="subpage-banner-overlay">
            {!isPetSitterOnly && (
              <button className="subpage-back-btn" onClick={() => setPage('main')}>
                ← Назад
              </button>
            )}
            <div className="subpage-banner-bottom">
              <div className="subpage-banner-label">🤝 Для пет ситтера</div>
              <h1 className="banner-name">{pet.name}</h1>
            </div>
          </div>
        </div>

        <PetSitterBlock petId={pet.id} standalone {...docProps} />
      </div>
    );
  }

  return (
    <div className="pet-card">

      {/* Баннер */}
      <div className="pet-banner">
        <img src="/banner.jpg" alt="banner" className="banner-img" />
        <div className="banner-overlay">
          <h1 className="banner-name">{pet.name}</h1>
        </div>
      </div>

      {/* Навигационная панель */}
      <NavBar onUpload={pet.role === 'owner' ? () => setUploadOpen(true) : null} onNavigate={setPage} />

      {/* Основная информация */}
      <section className="card-section" id="section-info">
        <SectionHeader
          title="Основная информация"
          pdfPath={`/api/pets/${petId}/card/pdf`}
          pdfName={`${pet.name}_карта.pdf`}
          periodMode="none"
          sectionKey="info"
          {...docProps}
        />
        <InfoRow label="Пол" value={pet.gender} />
        <InfoRow label="Порода" value={pet.breed} />
        <InfoRow label="Дата рождения" value={pet.birth_date} />
        <InfoRow label="Возраст" value={pet.age} />
        <InfoRow label="Паспорт" value={pet.passport_number} />
        <InfoRow label="Микрочип №" value={pet.microchip_number} />
        <InfoRow label="Дата микрочипа" value={pet.microchip_date} />
        <InfoRow label="Клеймо" value={pet.tag_number} />
      </section>

      <RecentTreatmentsBlock pet={pet} petId={petId} docProps={docProps} />

      {/* Вес */}
      <WeightBlock
        petId={pet.id}
        pdfPath={`/api/pets/${petId}/weight/pdf`}
        pdfName={`${pet.name}_вес.pdf`}
        {...docProps}
      />

      {/* Уход */}
      <CareBlock
        petId={pet.id}
        pdfPath={`/api/pets/${petId}/care/pdf`}
        pdfName={`${pet.name}_уход.pdf`}
        {...docProps}
      />

      {/* Анализы */}
      <LabBlock petId={pet.id} {...docProps} />

      {/* Лечение */}
      <TreatmentBlock
        petId={pet.id}
        pdfPath={`/api/pets/${petId}/treatments/pdf`}
        pdfName={`${pet.name}_лечение.pdf`}
        {...docProps}
      />

      {/* Визиты к врачу */}
      <VisitsBlock
        petId={pet.id}
        {...docProps}
      />

      {/* Питание */}
      <NutritionBlock
        petId={pet.id}
        pdfPath={`/api/pets/${petId}/nutrition/pdf`}
        pdfName={`${pet.name}_питание.pdf`}
        {...docProps}
      />

      {/* Реакции на питание */}
      <FoodReactionBlock petId={pet.id} {...docProps} />

      {/* Расписание */}
      <ScheduleBlock
        petId={pet.id}
        {...docProps}
      />

      {/* Контакты */}
      <ContactsBlock petId={pet.id} {...docProps} />

      {/* Модалки */}
      {uploadOpen && (
        <UploadModal
          petId={petId}
          onClose={() => setUploadOpen(false)}
          onUploaded={handleUploaded}
        />
      )}
      {viewSection && (
        <SectionDocsModal
          petId={petId}
          section={viewSection}
          docs={docs[viewSection] || []}
          onClose={() => setViewSection(null)}
          onDeleted={(docId) => handleDeleted(viewSection, docId)}
          canDelete={pet.role === 'owner'}
        />
      )}

    </div>
  );
}
